import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { setPartnerProfile, setView } from './onboardingSlice';
import { fetchMyPartner } from './partnerApi';
import type { ChecklistStatus } from './checklistApi';

function statusChip(status?: ChecklistStatus | null) {
  if (status === 'COMPLETED') return { label: 'Done', color: 'success' as const };
  if (status === 'IN_PROGRESS') return { label: 'In progress', color: 'warning' as const };
  return { label: 'To do', color: 'default' as const };
}

function fmtWhen(value?: string | null) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const PartnerOverview: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { brandConfig, formData, partnerProfile, partnerConditionsAcceptedLocal } = useSelector((s: RootState) => s.onboarding);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const conditionsDone = partnerProfile?.partner_conditions_status === 'COMPLETED' || partnerConditionsAcceptedLocal;
  const detailsDone = Boolean(partnerProfile?.id);
  const agreementDone = partnerProfile?.partner_agreement_status === 'COMPLETED';

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      dispatch(setPartnerProfile(await fetchMyPartner()));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not refresh progress');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const waitingLive = partnerProfile?.partner_agreement_status === 'IN_PROGRESS';

  useEffect(() => {
    if (!waitingLive) return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        const row = await fetchMyPartner();
        if (!cancelled) dispatch(setPartnerProfile(row));
      } catch (_) { /* keep polling */ }
    };
    const id = window.setInterval(pull, 2000);
    const onFocus = () => { pull(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [dispatch, waitingLive]);

  const cards = useMemo(() => ([
    {
      n: '01',
      title: brandConfig?.partner_conditions?.title || 'Partner conditions',
      body: 'Pricing and partnership terms. Read them and accept to continue.',
      status: (conditionsDone ? 'COMPLETED' : 'NOT_STARTED') as ChecklistStatus,
      when: partnerProfile?.partner_conditions_accepted_at
        || (partnerConditionsAcceptedLocal && !partnerProfile?.partner_conditions_accepted_at ? 'Accepted this session' : null),
      locked: false,
      action: conditionsDone ? null : 'Review & accept',
      onAction: () => dispatch(setView('conditions')),
    },
    {
      n: '02',
      title: 'Your details',
      body: 'Company, contact and address for your distributor record.',
      status: (detailsDone ? 'COMPLETED' : 'NOT_STARTED') as ChecklistStatus,
      when: null,
      locked: !conditionsDone,
      action: detailsDone ? 'Update details' : 'Fill in details',
      onAction: () => dispatch(setView('partnerDetails')),
    },
    {
      n: '03',
      title: 'Partner agreement',
      body: 'Open the partner agreement, complete your details and sign.',
      status: (partnerProfile?.partner_agreement_status || 'NOT_STARTED') as ChecklistStatus,
      when: partnerProfile?.partner_agreement_completed_at,
      locked: !conditionsDone || !detailsDone,
      action: agreementDone ? null : 'Open agreement',
      onAction: () => dispatch(setView('agreement')),
    },
  ]), [agreementDone, brandConfig, conditionsDone, detailsDone, dispatch, partnerConditionsAcceptedLocal, partnerProfile]);

  const allDone = cards.every((c) => c.status === 'COMPLETED');

  return (
    <Box>
      <Typography sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 700, fontSize: { xs: 26, md: 32 }, lineHeight: 1.1, mb: 0.5 }}>
        Your partner onboarding
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        {formData.email || formData.phone
          ? `Signed in as ${formData.email || formData.phone}. `
          : ''}
        Review the conditions, confirm your distributor details, and sign the partner agreement. You can leave and come back with the same email or phone.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {allDone && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Partner onboarding is complete.
        </Alert>
      )}

      <Box sx={{ position: 'relative', pl: { xs: 0, sm: 1 } }}>
        {cards.map((card, idx) => {
          const chip = statusChip(card.status);
          const locked = card.locked;
          return (
            <Box
              key={card.n}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '48px 1fr', sm: '64px 1fr' },
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                {idx < cards.length - 1 && (
                  <Box sx={{
                    position: 'absolute', top: 44, bottom: -18, width: 2,
                    bgcolor: card.status === 'COMPLETED' ? 'primary.main' : 'grey.300',
                  }} />
                )}
                <Box sx={{
                  width: 40, height: 40, borderRadius: '50%',
                  border: '2px solid',
                  borderColor: card.status === 'COMPLETED' ? 'primary.main' : 'grey.400',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: '"Fraunces", Georgia, serif', fontWeight: 700, fontSize: 13,
                  bgcolor: card.status === 'COMPLETED' ? 'primary.main' : 'background.paper',
                  color: card.status === 'COMPLETED' ? 'primary.contrastText' : 'text.primary',
                  zIndex: 1,
                }}>
                  {card.n}
                </Box>
              </Box>
              <Box sx={{
                border: '1px solid',
                borderColor: locked ? 'grey.200' : 'grey.300',
                borderRadius: 2,
                p: 2,
                opacity: locked ? 0.55 : 1,
                bgcolor: 'background.paper',
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Typography sx={{ fontWeight: 700 }}>{card.title}</Typography>
                  <Chip size="small" label={chip.label} color={chip.color} />
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>{card.body}</Typography>
                {card.when && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    {String(card.when).startsWith('Accepted') ? card.when : `Completed ${fmtWhen(card.when)}`}
                  </Typography>
                )}
                {card.action && !locked && (
                  <Button variant="contained" sx={{ mt: 1.5 }} onClick={card.onAction}>
                    {card.action}
                  </Button>
                )}
                {locked && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Unlock this after the previous step.
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Button variant="text" onClick={refresh} disabled={loading} sx={{ mt: 1 }}>
        {loading ? 'Refreshing…' : 'Refresh status'}
      </Button>
    </Box>
  );
};

export default PartnerOverview;
